// @flow

import React from 'react';
import { camelCase, compact, get, isEmpty, sortBy } from 'lodash';
import { constants, messageFormat } from '@clubmed/utils';

import {
  type CMSProps,
  type LineProps,
} from 'Client/cms/toolkit/product-accommodations/productAccommodations.resolver';
import { type LabeledIcon } from 'Components/frameworks/labeled-icons-list/LabeledIconsList.jsx';
import { COLORS } from 'Components/utils/color/colors';
import { ICONS } from 'Components/utils/icon/icons';
import { ProductInformationSliderContainer } from 'Components/widgets/product-information-slider/ProductInformationSlider.container.jsx';
import { ProductRoomCard } from 'Components/widgets/product-room-card/ProductRoomCard.jsx';
import { ProductRoomDetail } from 'Components/widgets/product-room-detail/ProductRoomDetail.jsx';
import { COMFORT_TYPE_SUITE } from 'Utils/proposal/constants';

const { PRODUCT_TYPES, RANKINGS } = constants;
const DEFAULT_RANKING = RANKINGS.LOW;

type ProductAccommodationsIconsList = {
  surfaceItem: LabeledIcon,
  capacityItem: ?LabeledIcon,
  outsideFacilitiesItems: ?Array<LabeledIcon>,
  strengths: ?Array<AccommodationStrength>,
  viewItem: ?LabeledIcon,
};

export function getSurfaceIconItem(
  i18n: CMSProps,
  surface: ?string,
  roomType: string,
): LabeledIcon {
  return {
    icon: ICONS[roomType] || null,
    labels: [get(i18n, camelCase(roomType)), surface || (roomType && ' ') || ''],
  };
}

export function getCapacityIconItem(i18n: CMSProps, capacity: ?number): ?LabeledIcon {
  const peopleLabel = get(i18n, 'numberOfPeopleLabel');
  if (!capacity || !peopleLabel) {
    return null;
  }

  return {
    icon: ICONS.PEOPLE,
    labels: [
      get(i18n, 'capacityOfPeopleLabel'),
      messageFormat(peopleLabel).format({ count: capacity }),
    ],
  };
}

export function getViewIconItem(view?: AccommodationView): ?LabeledIcon {
  return view
    ? {
        icon: ICONS[get(view, 'id')] || ICONS.TRIDENT1,
        labels: [view.label],
      }
    : null;
}

export function getOutsideFacilitiesIconItems(
  outsideFacilities?: Array<AccommodationEquipment>,
): ?Array<LabeledIcon> {
  if (isEmpty(outsideFacilities)) {
    return null;
  }

  return outsideFacilities.map(item => {
    return {
      icon: ICONS[get(item, 'id')],
      labels: [item.label],
    };
  });
}

function getServiceIconItem(service): ?LabeledIcon {
  return service
    ? {
        icon: ICONS[get(service, 'id')] || ICONS.TRIDENT1,
        labels: [service.label],
      }
    : null;
}

export function aggregateIconList({
  surfaceItem,
  capacityItem,
  outsideFacilitiesItems,
  strengths,
  viewItem,
}: ProductAccommodationsIconsList) {
  const maxAccomodationsToDisplay = 4;
  const accomodationsWithoutStrengthsCount = [
    surfaceItem,
    capacityItem,
    ...(outsideFacilitiesItems || []),
    viewItem,
  ].filter(item => get(item, 'labels.0')).length;

  const availableSpacesForStrengths =
    maxAccomodationsToDisplay - accomodationsWithoutStrengthsCount;
  const strengthsItems = (strengths || []).slice(0, availableSpacesForStrengths).map(strength => ({
    icon: ICONS[get(strength, 'id')] || ICONS.TRIDENT1,
    labels: [get(strength, 'label') || ''],
  }));

  // $FlowFixMe
  return compact(
    ([
      surfaceItem,
      capacityItem,
      ...(outsideFacilitiesItems || []),
      ...strengthsItems,
      viewItem,
    ]: Array<?LabeledIcon>),
  )
    .filter(({ labels = [] }) => labels.length && labels[0])
    .slice(0, 4);
}

function mapAccommodationStrengths(i18n: CMSProps, accommodation: Accommodation) {
  const { maxCapacity, roomType, strengths, surface, view, outsideFacilities } = accommodation;
  const surfaceItem = getSurfaceIconItem(i18n, surface, roomType);
  const capacityItem = getCapacityIconItem(i18n, maxCapacity);
  const viewItem = getViewIconItem(view);
  const outsideFacilitiesItems = getOutsideFacilitiesIconItems(outsideFacilities);

  return aggregateIconList({
    surfaceItem,
    capacityItem,
    outsideFacilitiesItems,
    strengths,
    viewItem,
  });
}

export function mapAccommodationServices(
  i18n: CMSProps,
  accommodation: Accommodation,
  isExclusiveCollection: boolean,
): Array<{
  label: string | null,
  items: Array<?LabeledIcon> | boolean,
}> {
  const { bathroomEquipments = [], equipments = [], services = [] } = accommodation;
  const isSuiteAccommodation = accommodation?.comfortId === COMFORT_TYPE_SUITE;

  return [
    {
      label: isExclusiveCollection || isSuiteAccommodation ? get(i18n, 'servicesLabel', '') : null,
      items: Array.isArray(services) && services.map(service => getServiceIconItem(service)),
    },
    {
      label: get(i18n, 'equipmentLabel', ''),
      items: equipments.map(equipment => getServiceIconItem(equipment)),
    },
    {
      label: get(i18n, 'bathroomLabel', ''),
      items: bathroomEquipments.map(equipment => getServiceIconItem(equipment)),
    },
    {
      label: !(isExclusiveCollection || isSuiteAccommodation)
        ? get(i18n, 'servicesLabel', '')
        : null,
      items: Array.isArray(services) && services.map(service => getServiceIconItem(service)),
    },
  ].filter(service => service.label);
}

function getRankingIndex(ranking) {
  return Object.values(RANKINGS).findIndex(r => r === ranking);
}

function isProductExclusiveCollection(comfort) {
  return comfort > 4;
}

// eslint-disable-next-line clubmed/no-fake-component
function getAccommodation({ accommodation, categoryLabel, resortComfort, i18n }) {
  const {
    categoryId,
    comfortId,
    comfortType,
    description,
    id,
    label,
    media: { photos },
  } = accommodation;

  const strengths = mapAccommodationStrengths(i18n, accommodation);
  const isDisplayable = [strengths.length, comfortType, label, description].every(item => !!item);
  const isExclusiveCollection =
    isProductExclusiveCollection(resortComfort) || accommodation.exclusiveCollection;

  const details = {
    categoryId,
    comfort: resortComfort,
    comfortType,
    comfortId,
    description,
    immersive: {
      url: get(photos, '[0].url'),
    },
    label,
    overview: strengths,
    services: mapAccommodationServices(i18n, accommodation, isExclusiveCollection),
    slideshow: {
      groupeKey: 'roomCard',
      media: photos.map(({ url }) => ({ src: url })),
    },
  };

  return (
    isDisplayable && (
      <ProductRoomCard
        fetchDetail={() => Promise.resolve(details)}
        isExclusiveCollection={isExclusiveCollection}
        key={id}
        labeledIconsList={strengths}
        trackingData={{
          categoryGA: 'resort',
          actionGA: 'blockInfos',
          labelGA: `accommodation_${categoryLabel}_seeMore`,
        }}
        media={photos.map(({ url }) => ({ src: url }))}
        seeMoreLabel={i18n.seeMore}
        title={label}
      />
    )
  );
}

function renderDetail() {
  return ({
    comfort,
    comfortType,
    description,
    exclusiveCollection,
    immersive,
    label,
    overview,
    services,
    slideshow,
  }) => {
    const customProps = {
      description,
      title: label,
      diamond: {
        color: COLORS.LILAS,
        label: comfortType,
      },
      immersive,
      isExclusiveCollection: isProductExclusiveCollection(comfort) || exclusiveCollection,
      overview,
      services,
      slideshow,
    };

    return <ProductRoomDetail {...customProps} />;
  };
}

export function mapItemForTabs(props: LineProps) {
  const {
    accommodationCategory: { rankedCategories = [] } = {},
    bathroomLabel,
    capacityOfPeopleLabel,
    config: { common: { backNavigation, scrollBackward, scrollForward } = {} } = {},
    equipmentLabel,
    isLineExclusiveCollection,
    numberOfPeopleLabel,
    resort: { accommodations = [], comfort: resortComfort = 3, type },
    roomConfiguration: { singleRoom, connectingRooms, separatedRooms } = {},
    seeMore,
    servicesLabel,
  } = props;

  const i18n = {
    bathroomLabel,
    capacityOfPeopleLabel,
    connectingRooms,
    equipmentLabel,
    numberOfPeopleLabel,
    seeMore,
    separatedRooms,
    servicesLabel,
    singleRoom,
  };

  const accommodationCategories = accommodations.reduce(
    (categories: {}, accommodation: Accommodation) => {
      // Ranking index to sort categories and accommodations within a category
      const rankedCategory = rankedCategories.find(c => c.id === accommodation.categoryId);
      const categoryRanking = rankedCategory ? rankedCategory.ranking : DEFAULT_RANKING;
      const categoryIndex = getRankingIndex(categoryRanking);

      // Accommodation tabs are now based on accommodation comfort, not category...
      let categoryId = accommodation.comfortId;
      let categoryLabel = accommodation.comfortType;
      // ...except for VILLA products
      if (type === PRODUCT_TYPES.VILLA) {
        // eslint-disable-next-line prefer-destructuring
        categoryId = accommodation.categoryId;
        categoryLabel = accommodation.category;
      }

      if (!categoryId) return categories;

      // Ranking index to sort accommodations within a category
      const accommodationIndex = getRankingIndex(accommodation.ranking);
      const accommodationProps = {
        accommodation,
        accommodationIndex,
        categoryLabel,
        categoryIndex,
        colorId: accommodation.categoryId,
        resortComfort,
        i18n,
      };

      if (categories[categoryId]) {
        // Add accommodation to category if it exists
        categories[categoryId].categoryAccommodations.push(accommodationProps);
      } else {
        // Create category if it doesn't exist
        categories[categoryId] = {
          categoryAccommodations: [accommodationProps],
          categoryId,
          categoryIndex,
          categoryLabel,
        };
      }
      return categories;
    },
    {},
  );
  return (
    // I have no idea what's going on there and to be honest I don't care enough
    // sorry to the person who'll be reading that in the future but I give up
    // $FlowFixMe
    Object.values(accommodationCategories)
      // $FlowFixMe
      .sort((a, b) => b.categoryIndex - a.categoryIndex)
      .map((accommodationCategory, index) => {
        // $FlowFixMe
        const { categoryAccommodations, categoryLabel } = accommodationCategory;
        // We need to sort with the upper index first. Since Array.sort is broken client side (why??),
        // we use the lodash function which only sorts from the lower to the upper. We knows that the
        // accomodations are already sorted by accomodationIdex so we have to sort by categoryIndex.
        // The tricky part is here: we cannot reverse the output of lodash.sortBy, so we sort with a
        // negative categoryIndex.
        const categoryAccommodationsProps = sortBy(
          [...categoryAccommodations],
          ({ categoryIndex }) => -1 * categoryIndex,
        );

        const content = categoryAccommodationsProps.map(getAccommodation).filter(Boolean);
        const isLarge = content.every(item => item.props.isExclusiveCollection);

        return (isLineExclusiveCollection && isLarge) || !isLineExclusiveCollection
          ? {
              header: {
                title: categoryLabel,
                trackingData: {
                  categoryGA: 'resort',
                  actionGA: 'blockInfos',
                  labelGA: `accommodations_${categoryLabel}`,
                },
              },
              content: [
                <ProductInformationSliderContainer
                  backNavigationLabel={backNavigation}
                  errorMessage="error"
                  isLarge={isLarge}
                  key={`ProductAccommodationsSlider_${categoryLabel}`}
                  queryLayer="acommodationLayer"
                  renderDetail={renderDetail()}
                  scrollBackwardLabel={scrollBackward}
                  scrollForwardLabel={scrollForward}
                  sliderIndex={index}
                >
                  {content}
                </ProductInformationSliderContainer>,
              ],
            }
          : null;
      })
      .filter(item => item !== null)
  );
}
